import React, { useState, useEffect } from 'react';
import css from './css.module.less';
import Query, { EnumQueryType } from 'src/constrains/Query1';
import {
  useQueries,
  useMultiPageSelectRows,
} from '@/src/tools/hooks/listPageHooks';
import { SizeFittingTable, ModalFooter } from 'src/components/Antd';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { convertQuery, filterList, generateColumns } from './utils';
import { usePaginationAndSort } from '@/src/hooks/usePaginationAndSort';
import GlobalPagination from '@/src/components/StyledPagination/GlobalPagination';
import { getCustomerSummaries } from 'src/request/voucher';
import { FetchCustomerSummariesParams } from 'src/types/customer';
import { CustomerSummary } from 'src/types/customer';

const Modal = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { selected, onCancel, onConfirm, tags, dbUuid } = props;

  const [allCustomers, setAllCustomers] = useState<CustomerSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const [persistedSelectedObjectsMap, setPersistedSelectedObjectsMap] =
    useState<Map<string, CustomerSummary>>(new Map());

  const {
    currentPage,
    pageSize,
    sortBy,
    totalItems,
    firstRow,
    pageSizeOptions,
    setTotalItems,
    handlePageChange,
    handlePageSizeChange,
    handleTableChange,
  } = usePaginationAndSort();

  const { queryProps, search } = useQueries(
    filterList(tags || [], { getI18n }),
    [],
    convertQuery,
  );

  const currentInitialSelectedKeys = React.useMemo(
    () => (selected || []).map((i) => i.uuid),
    [selected],
  );

  useEffect(() => {
    const initialMap = new Map<string, CustomerSummary>();
    (selected || []).forEach((customer) => {
      if (customer && customer.uuid) {
        initialMap.set(customer.uuid, customer);
      }
    });
    setPersistedSelectedObjectsMap(initialMap);
  }, [selected]);

  const {
    rowSelectionProps: hookInternalRowSelectionProps,
    allSelectedRowKeys,
  } = useMultiPageSelectRows<CustomerSummary>(
    allCustomers,
    currentInitialSelectedKeys,
    'uuid',
  );

  const rowSelectionProps = {
    ...hookInternalRowSelectionProps,
    onSelect: (record: CustomerSummary, selectedOp: boolean) => {
      if (hookInternalRowSelectionProps.onSelect) {
        hookInternalRowSelectionProps.onSelect(record, selectedOp);
      }
      if (selectedOp) {
        setPersistedSelectedObjectsMap((prevMap) => {
          const newMap = new Map(prevMap);
          newMap.set(record.uuid, record);
          return newMap;
        });
      }
    },
    onSelectAll: (
      selectedOp: boolean,
      selRows: CustomerSummary[],
      changeRows: CustomerSummary[],
    ) => {
      if (hookInternalRowSelectionProps.onSelectAll) {
        hookInternalRowSelectionProps.onSelectAll(
          selectedOp,
          selRows,
          changeRows,
        );
      }
      if (selectedOp) {
        setPersistedSelectedObjectsMap((prevMap) => {
          const newMap = new Map(prevMap);
          changeRows.forEach((item) => newMap.set(item.uuid, item));
          return newMap;
        });
      }
    },
  };

  const col = React.useMemo(() => generateColumns(getI18n), [getI18n]);

  useEffect(() => {
    const fetchData = async () => {
      if (!dbUuid) return;
      setLoading(true);
      try {
        const apiParams: FetchCustomerSummariesParams = {
          searchTextList: search?.searchText,
          customerTags: search?.customerTags,
          sortBy: sortBy,
          firstRow: firstRow,
          countPerPage: pageSize,
        };

        const payload = await getCustomerSummaries(dbUuid, apiParams);
        setAllCustomers(payload.results || []);
        setTotalItems(payload.totalCount || 0);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setAllCustomers([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dbUuid, currentPage, pageSize, sortBy, search, firstRow, setTotalItems]);

  const handleOnSearchChange = (e) => {
    if (queryProps.onSearchChange) {
      queryProps.onSearchChange({ search: e, type: EnumQueryType.search });
      handlePageChange(1);
    }
  };

  const handleOnFilterChange = (e) => {
    if (queryProps.onSearchChange) {
      queryProps.onSearchChange({ filter: e, type: EnumQueryType.filter });
      handlePageChange(1);
    }
  };

  const handleOnTagClose = (e) => {
    if (queryProps.onSearchChange) {
      queryProps.onSearchChange({ item: e, type: EnumQueryType.closeTag });
      handlePageChange(1);
    }
  };

  const handleOnConfirm = () => {
    const customersToConfirm: CustomerSummary[] = [];
    allSelectedRowKeys.forEach((key) => {
      if (persistedSelectedObjectsMap.has(key)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        customersToConfirm.push(persistedSelectedObjectsMap.get(key)!);
      } else {
        console.warn(
          `Customer object for key ${key} not found in persistedSelectedObjectsMap.`,
        );
      }
    });

    if (onConfirm) {
      onConfirm(customersToConfirm);
    }
  };

  return (
    <React.Fragment>
      <div className={css.container}>
        <div className={css.queryContainer}>
          <Query
            onSearchChange={handleOnSearchChange}
            onFilterChange={handleOnFilterChange}
            onTagClose={handleOnTagClose}
            filters={queryProps.filters || []}
            sortList={queryProps.sortList || []}
            data={queryProps.searchConditions || []}
          />
        </div>
        <SizeFittingTable
          className={css.modalTableBody}
          id='SalesChannelDiscountCustomerModalTable'
          dataSource={allCustomers}
          rowSelection={rowSelectionProps}
          pagination={false}
          columns={col}
          rowKey={'uuid'}
          scroll={{ x: 'max-content', y: 392 }}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <div className={css.paginationWrapper}>
        <GlobalPagination
          current={currentPage}
          total={totalItems}
          pageSize={pageSize}
          pageOnChange={handlePageChange}
          pageSizeChange={handlePageSizeChange}
          pageSizeOption={pageSizeOptions}
          paginationProps={{ showQuickJumper: false }}
        />
      </div>
      <ModalFooter
        okText={getI18n('general.confirm')}
        cancelText={getI18n('general.cancel')}
        onOk={handleOnConfirm}
        onCancel={onCancel}
        okButtonProps={{ disabled: allSelectedRowKeys.length === 0 }}
      />
    </React.Fragment>
  );
};

export default Modal;
